import { useCallback, useEffect, useState } from 'react';

type AtomValueGetter<T> = (get: <U>(atom: Atom<U>) => Promise<U>) => Promise<T>;

/**
 * Deep equality check for atom values. Handles primitives, bigints, arrays, plain objects,
 * and objects with an `equals()` method (e.g., Decimal instances).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a === 'bigint') return false; // a !== b already checked above

  if (typeof a === 'object') {
    // Handle objects with an equals() method (e.g., Decimal)
    if ('equals' in a && typeof a.equals === 'function') {
      return a.equals(b);
    }

    if (Array.isArray(a)) {
      if (!Array.isArray(b) || a.length !== b.length) return false;
      return a.every((item: unknown, i: number) => deepEqual(item, (b as unknown[])[i]));
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every(key => deepEqual(a[key], b[key]));
  }

  return false;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const atoms: Atom<any>[] = [];

export class Atom<T> {
  private value: AtomValueGetter<T>;
  private resolvedValue: T | undefined = undefined;
  private fetchPromise: Promise<T> | null = null;

  private subscribers: ((newValue: T) => void)[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private childAtoms: Atom<any>[] = [];
  public refreshEvents: string[] = [];

  /**
   * Counter-based refresh pause mechanism. While > 0, refresh() calls are deferred.
   * Uses a counter (not boolean) to support multiple concurrent pausers (e.g. overlapping transactions).
   */
  private refreshPauseCount = 0;
  private refreshDeferred = false;

  /** True while a refresh() is in-flight. Used to coalesce concurrent refresh calls. */
  private refreshInProgress = false;

  private _lastError: unknown = null;
  private _errorSubscribers: ((error: unknown) => void)[] = [];
  private _errorRetryTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(value: AtomValueGetter<T>, pollIntervalMs?: number, refreshEvents?: string[]) {
    this.value = value;

    if (pollIntervalMs) {
      // Stagger the start of each atom's polling timer by a random offset
      // (up to 10 s). Without this, all atoms created during module init start
      // their timers at nearly the same instant, and atoms whose intervals are
      // multiples of each other (30 s, 60 s, 120 s …) periodically re-align —
      // creating bursts of concurrent RPC requests that can overwhelm the node.
      const jitter = Math.floor(Math.random() * Math.min(pollIntervalMs, 10_000));
      setTimeout(() => {
        setInterval(() => {
          this.refresh();
        }, pollIntervalMs);
      }, jitter);
    }

    if (refreshEvents) {
      this.refreshEvents = refreshEvents;
    }
  }

  /**
   * Pause all refresh calls (polling + event-driven). While paused, any refresh attempts
   * are deferred and will execute once when resumeRefresh() brings the count back to 0.
   * Call resumeRefresh() exactly once for each pauseRefresh() call.
   */
  pauseRefresh() {
    this.refreshPauseCount++;
  }

  /**
   * Resume refreshes after a pauseRefresh() call. When the last pause is lifted
   * and refreshes were deferred, triggers a single refresh to sync with the latest state.
   */
  resumeRefresh() {
    this.refreshPauseCount = Math.max(0, this.refreshPauseCount - 1);
    if (this.refreshPauseCount === 0 && this.refreshDeferred) {
      this.refreshDeferred = false;
      this.refresh();
    }
  }

  setError(error: unknown) {
    this._lastError = error;
    this._errorSubscribers.forEach(cb => cb(error));
    this.scheduleErrorRetry();
  }

  clearError() {
    if (this._lastError !== null) {
      this._lastError = null;
      this._errorSubscribers.forEach(cb => cb(null));
    }
    if (this._errorRetryTimeoutId) {
      clearTimeout(this._errorRetryTimeoutId);
      this._errorRetryTimeoutId = null;
    }
  }

  getLastError(): unknown {
    return this._lastError;
  }

  subscribeError(callback: (error: unknown) => void) {
    this._errorSubscribers.push(callback);
    return () => {
      this._errorSubscribers = this._errorSubscribers.filter(cb => cb !== callback);
    };
  }

  private scheduleErrorRetry() {
    if (this._errorRetryTimeoutId) return;
    this._errorRetryTimeoutId = setTimeout(() => {
      this._errorRetryTimeoutId = null;
      this.refresh();
    }, 5_000);
  }

  getResolvedValue() {
    return this.resolvedValue;
  }

  getAtomValue<U>(atom: Atom<U>): Promise<U> {
    if (!atom.childAtoms.includes(this)) {
      atom.childAtoms.push(this);
    }

    const resolvedValue = atom.getResolvedValue();
    if (resolvedValue !== undefined) {
      return Promise.resolve(resolvedValue);
    } else {
      return atom.getValue();
    }
  }

  getValue() {
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    this.fetchPromise = this.value(this.getAtomValue.bind(this));

    this.fetchPromise.then(
      result => {
        this.resolvedValue = result;
      },
      () => {
        // Clear cached promise on failure so subsequent getValue() calls can retry
        // instead of returning the same rejected promise forever.
        // This also prevents an unhandled promise rejection from the dangling .then() chain.
        this.fetchPromise = null;
      },
    );

    return this.fetchPromise;
  }

  notifyChildAtoms() {
    this.childAtoms.forEach(childAtom => {
      childAtom.refresh();
    });
  }

  notifyComponents(value: T) {
    this.subscribers.forEach(subscriber => subscriber(value));
  }

  async refresh() {
    if (this.refreshPauseCount > 0) {
      this.refreshDeferred = true;
      return;
    }

    // Coalesce concurrent refresh calls — if one is already in-flight (e.g. from a
    // poll timer firing while event-driven refreshes are pending), defer instead of
    // launching parallel RPC requests that can overwhelm the node and cause
    // "Failed to fetch" errors.
    if (this.refreshInProgress) {
      this.refreshDeferred = true;
      return;
    }

    this.refreshInProgress = true;
    try {
      const result = await this.value(this.getAtomValue.bind(this));

      this.setValue(result);
    } catch (error) {
      // Silently ignore transient errors during background refresh (network failures, 429s, etc.).
      // The atom keeps its last resolved value and the next poll cycle will retry.
      console.warn('Atom refresh failed, keeping previous value:', error);
    } finally {
      this.refreshInProgress = false;

      // If one or more refreshes were requested while we were busy, run exactly
      // one more to pick up the latest state.
      if (this.refreshDeferred) {
        this.refreshDeferred = false;
        this.refresh();
      }
    }
  }

  setValue(value: T) {
    if (deepEqual(this.resolvedValue, value)) return;

    this.resolvedValue = value;

    this.notifyComponents(value);
    this.notifyChildAtoms();
  }

  subscribe(callback: (newValue: T) => void) {
    this.subscribers.push(callback);

    // Unsubscribe function
    return () => {
      this.subscribers = this.subscribers.filter(subscriber => subscriber !== callback);
    };
  }
}

const wrapPromise = <T>(
  promise: Promise<T> & {
    status?: 'pending' | 'fulfilled' | 'rejected';
    value?: T;
    reason?: unknown;
  },
) => {
  if (promise.status === 'pending') {
    throw promise;
  } else if (promise.status === 'fulfilled') {
    return promise.value as T;
  } else if (promise.status === 'rejected') {
    throw promise.reason;
  } else {
    promise.status = 'pending';
    promise.then(
      v => {
        promise.status = 'fulfilled';
        promise.value = v;
      },
      e => {
        promise.status = 'rejected';
        promise.reason = e;
      },
    );
    throw promise;
  }
};

const useAtomValue = <T>(atom: Atom<T>) => {
  let resolvedValue = atom.getResolvedValue();
  if (resolvedValue === undefined) {
    const value: Promise<T> = atom.getValue();
    if (value) {
      resolvedValue = wrapPromise(value);
    }
  }

  const [state, setState] = useState(resolvedValue as T);

  useEffect(() => {
    const unsubscribe = atom.subscribe(setState);

    return () => {
      unsubscribe();
    };
  }, [atom]);

  return state;
};

const useAtomSetter = <T>(atom: Atom<T>) => {
  return useCallback((value: T) => atom.setValue(value), [atom]);
};

const useAtom = <T>(atom: Atom<T>) => {
  return [useAtomValue(atom), useAtomSetter(atom)] as const;
};
export { useAtom };

const useAtomError = <T>(atom: Atom<T>) => {
  const [error, setError] = useState<unknown>(atom.getLastError());

  useEffect(() => {
    const unsubscribe = atom.subscribeError(setError);
    return () => unsubscribe();
  }, [atom]);

  return error;
};
export { useAtomError };

const createAsyncAtom = <T>(
  callback: (get: <U>(atom: Atom<U>) => Promise<U>) => Promise<T>,
  pollIntervalMs?: number,
  refreshEvents?: string[],
) => {
  const atom = new Atom(callback, pollIntervalMs, refreshEvents);

  atoms.push(atom);

  return atom;
};
export { createAsyncAtom };

const emitAtomEvent = (eventName: string) => {
  const promises: Promise<void>[] = [];

  atoms.forEach(atom => {
    if (atom.refreshEvents.includes(eventName)) {
      promises.push(atom.refresh());
    }
  });

  return Promise.all(promises);
};
export { emitAtomEvent };
